/** @format */

import React, { useMemo, useState, useEffect } from 'react';
import { useFreeVideos } from '../hooks/useFreeVideos';
import YouTubePlayer from '../components/YouTubePlayer';
import { useAuth } from '../contexts/AuthContext';
import { renderTextWithLinks } from '../utils/formatUtils';
import CustomDropdown from '../components/CustomDropdown';
import toast from 'react-hot-toast';
import Pagination from '../components/Pagination';
import AIChatButton from '../components/AIChatButton';
import { extractVideoId, fetchTranscript, getTranscriptText } from '../utils/transcriptUtils';

const FreeResources: React.FC = () => {
  const { videos, loading, error, addComment, getTopics, addVideo } = useFreeVideos();
  const { currentUser } = useAuth();
  const [selectedTopic, setSelectedTopic] = useState<string>('all');
  const [commentDrafts, setCommentDrafts] = useState<Record<string, string>>({});
  const [newTitle, setNewTitle] = useState<string>('');
  const [newUrl, setNewUrl] = useState<string>('');
  const [newTopic, setNewTopic] = useState<string>('');
  const [newDescription, setNewDescription] = useState<string>('');
  const [newTags, setNewTags] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [showBulk, setShowBulk] = useState<boolean>(false);
  const [bulkText, setBulkText] = useState<string>('');
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(6);
  const [transcripts, setTranscripts] = useState<Record<string, string>>({});
  const [loadingTranscripts, setLoadingTranscripts] = useState<Record<string, boolean>>({});

  const channelUrl = process.env.REACT_APP_YOUTUBE_CHANNEL_URL;
  const normalizedRole = (currentUser?.role || '').toString().trim().toLowerCase();
  const isAdmin = normalizedRole === 'admin';

  const detectDelimiter = (text: string): ',' | '\t' => {
    const commaCount = (text.match(/,/g) || []).length;
    const tabCount = (text.match(/\t/g) || []).length;
    return tabCount > commaCount ? '\t' : ',';
  };

  const parseSeparatedValues = (input: string, delimiter: ',' | '\t'): string[][] => {
    const lines = input
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    const rows: string[][] = [];
    for (const line of lines) {
      const values: string[] = [];
      let current = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          // Toggle quotes or escape double quote within quotes
          if (inQuotes && line[i + 1] === '"') {
            current += '"';
            i++; // skip next quote
          } else {
            inQuotes = !inQuotes;
          }
        } else if (char === delimiter && !inQuotes) {
          values.push(current);
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current);
      rows.push(values.map((v) => v.trim()));
    }
    return rows;
  };

  const parseBulkResources = (text: string) => {
    const delimiter = detectDelimiter(text);
    const rows = parseSeparatedValues(text, delimiter);
    if (rows.length === 0) return [] as any[];
    const headerCandidates = rows[0].map((h) => h.toLowerCase());
    const hasHeader = headerCandidates.includes('title') || headerCandidates.includes('youtubeurl') || headerCandidates.includes('youtube_url');
    const dataRows = hasHeader ? rows.slice(1) : rows;

    const getIndex = (name: string): number => {
      const idx = headerCandidates.indexOf(name);
      return idx >= 0 ? idx : -1;
    };

    const indices = hasHeader
      ? {
          title: Math.max(getIndex('title'), getIndex('name')),
          youtubeUrl: Math.max(getIndex('youtubeurl'), getIndex('youtube_url'), getIndex('url')),
          topic: getIndex('topic'),
          description: Math.max(getIndex('description'), getIndex('desc')),
          tags: getIndex('tags'),
        }
      : { title: 0, youtubeUrl: 1, topic: 2, description: 3, tags: 4 };

    const items = dataRows.map((cols) => {
      const title = (cols[indices.title] || '').trim();
      const youtubeUrl = (cols[indices.youtubeUrl] || '').trim();
      const topic = (indices.topic >= 0 ? cols[indices.topic] : '').trim();
      const description = (indices.description >= 0 ? cols[indices.description] : '').trim();
      const tagsRaw = (indices.tags >= 0 ? cols[indices.tags] : '').trim();
      const tags = tagsRaw
        ? tagsRaw
            .split(',')
            .map((t) => t.trim())
            .filter((t) => t.length > 0)
        : [];
      return { title, youtubeUrl, topic: topic || undefined, description: description || undefined, tags: tags.length ? tags : undefined };
    });
    return items.filter((it) => it.title && it.youtubeUrl);
  };

  const filteredVideos = useMemo(() => {
    if (selectedTopic === 'all') return videos;
    return videos.filter((v) => (v.topic || '').toLowerCase() === selectedTopic.toLowerCase());
  }, [videos, selectedTopic]);

  const topics = useMemo(() => ['all', ...getTopics()], [getTopics, videos.length]);

  // Reset to first page when filters or data change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedTopic, videos.length, itemsPerPage]);

  const handleFetchTranscript = async (videoId: string, youtubeUrl: string) => {
    const extractedVideoId = extractVideoId(youtubeUrl);
    if (!extractedVideoId) {
      toast.error('Invalid YouTube URL');
      return;
    }

    if (transcripts[videoId]) {
      return; // Already fetched
    }

    try {
      setLoadingTranscripts(prev => ({ ...prev, [videoId]: true }));
      const transcript = await fetchTranscript(extractedVideoId);
      const transcriptText = getTranscriptText(transcript);
      setTranscripts(prev => ({ ...prev, [videoId]: transcriptText }));
      toast.success('Transcript loaded successfully');
    } catch (error: any) {
      console.error('Error fetching transcript:', error);
      
      // Check if it's a transcript not available error
      if (error.message && error.message.includes('Transcript is disabled')) {
        setTranscripts(prev => ({ ...prev, [videoId]: 'NO_TRANSCRIPT' }));
        toast.error('Transcript not available for this video');
      } else {
        toast.error('Failed to load transcript');
      }
    } finally {
      setLoadingTranscripts(prev => ({ ...prev, [videoId]: false }));
    }
  };

  const totalItems = filteredVideos.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const paginatedVideos = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return filteredVideos.slice(start, end);
  }, [filteredVideos, currentPage, itemsPerPage]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Hero Header */}
      <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 md:py-16">
          <div className="text-center">
            <div className="flex flex-col sm:flex-row items-center justify-center mb-4 sm:mb-6">
              <div className="p-2 sm:p-3 bg-white/20 backdrop-blur-sm rounded-full mb-3 sm:mb-0 sm:mr-4">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
                Free Learning Resources
              </h1>
        </div>
            <p className="text-base sm:text-lg md:text-xl text-blue-100 mb-6 sm:mb-8 max-w-3xl mx-auto leading-relaxed px-2">
              Discover curated educational content from top creators. Access high-quality learning materials 
              organized by topic, complete with AI-powered insights and interactive discussions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center px-4">
        {channelUrl && (
          <a
            href={channelUrl}
            target="_blank"
            rel="noopener noreferrer"
                  className="w-full sm:w-auto inline-flex items-center justify-center px-4 sm:px-6 py-3 rounded-full bg-white text-blue-600 text-base sm:text-lg font-semibold hover:bg-blue-50 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
          >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
            Subscribe on YouTube
          </a>
        )}
              <div className="flex flex-col sm:flex-row items-center text-white/90 text-center sm:text-left">
                <div className="flex -space-x-2 mb-2 sm:mb-0 sm:mr-3">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-400 rounded-full border-2 border-white"></div>
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-purple-400 rounded-full border-2 border-white"></div>
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-indigo-400 rounded-full border-2 border-white"></div>
                </div>
                <span className="text-xs sm:text-sm">Join thousands of learners</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/10 rounded-full"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-white/10 rounded-full"></div>
          <div className="absolute top-20 left-20 w-20 h-20 bg-white/10 rounded-full"></div>
          <div className="absolute bottom-20 right-20 w-20 h-20 bg-white/10 rounded-full"></div>
        </div>
      </div>        

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-2 sm:py-12">

      {isAdmin && (
        <div className="mb-8 bg-white/80 backdrop-blur-sm border border-white/20 rounded-2xl p-4 sm:p-6 shadow-xl">
          <div className="flex flex-col sm:flex-row items-start sm:items-center mb-6">
            <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg mb-3 sm:mb-0 sm:mr-3">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Add a Free Resource
            </h2>
          </div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="flex flex-col">
                <label className="text-sm font-semibold text-gray-700 mb-2">Title</label>
              <input
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="e.g., Financial Accounting Basics"
                  className="px-3 sm:px-4 py-3 border border-gray-300 rounded-lg text-sm bg-white/60 backdrop-blur-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                disabled={saving}
              />
            </div>
            <div className="flex flex-col">
                <label className="text-sm font-semibold text-gray-700 mb-2">YouTube URL</label>
              <input
                type="url"
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
                  className="px-3 sm:px-4 py-3 border border-gray-300 rounded-lg text-sm bg-white/60 backdrop-blur-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                disabled={saving}
              />
            </div>
            <div className="flex flex-col">
                <label className="text-sm font-semibold text-gray-700 mb-2">Topic (optional)</label>
              <input
                type="text"
                value={newTopic}
                onChange={(e) => setNewTopic(e.target.value)}
                placeholder="e.g., ICAG"
                  className="px-3 sm:px-4 py-3 border border-gray-300 rounded-lg text-sm bg-white/60 backdrop-blur-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                disabled={saving}
              />
            </div>
            <div className="flex flex-col">
                <label className="text-sm font-semibold text-gray-700 mb-2">Tags (comma-separated, optional)</label>
              <input
                type="text"
                value={newTags}
                onChange={(e) => setNewTags(e.target.value)}
                placeholder="accounting, basics, exam prep"
                  className="px-3 sm:px-4 py-3 border border-gray-300 rounded-lg text-sm bg-white/60 backdrop-blur-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                disabled={saving}
              />
            </div>
              <div className="lg:col-span-2 flex flex-col">
                <label className="text-sm font-semibold text-gray-700 mb-2">Description (optional)</label>
              <textarea
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Short description of the video"
                  className="px-3 sm:px-4 py-3 border border-gray-300 rounded-lg text-sm min-h-[80px] sm:min-h-[100px] bg-white/60 backdrop-blur-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 resize-none"
                disabled={saving}
              />
            </div>
          </div>
                      <div className="mt-6 flex justify-center sm:justify-end">
            <button
              onClick={async () => {
                if (!isAdmin) return;
                const title = newTitle.trim();
                const url = newUrl.trim();
                if (!title) {
                  toast.error('Title is required');
                  return;
                }
                if (!url) {
                  toast.error('YouTube URL is required');
                  return;
                }
                try {
                  setSaving(true);
                  const tags = newTags
                    .split(',')
                    .map((t) => t.trim())
                    .filter((t) => t.length > 0);
                  await addVideo({
                    title,
                    youtubeUrl: url,
                    topic: newTopic.trim() || undefined,
                    description: newDescription.trim() || undefined,
                    tags: tags.length ? tags : undefined,
                  });
                  setNewTitle('');
                  setNewUrl('');
                  setNewTopic('');
                  setNewDescription('');
                  setNewTags('');
                  toast.success('Resource added');
                } catch (err) {
                  console.error(err);
                  toast.error('Failed to add resource');
                } finally {
                  setSaving(false);
                }
              }}
              disabled={saving}
                className="w-full sm:w-auto px-6 sm:px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-base sm:text-lg font-semibold rounded-lg disabled:opacity-50 hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                {saving ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white mr-2"></div>
                    Adding Resource...
                  </div>
                ) : (
                  'Add Resource'
                )}
            </button>
          </div>

          {/* Bulk Import */}
          <div className="mt-6 border-t border-gray-200 pt-4">
            <div className="flex items-center justify-between">
              <h3 className="text-md font-semibold text-gray-900">Bulk Import</h3>
              <button
                onClick={() => setShowBulk((v) => !v)}
                className="text-primary-600 text-sm font-medium"
              >
                {showBulk ? 'Hide' : 'Show'}
              </button>
            </div>
            {showBulk && (
              <div className="mt-3">
                <p className="text-xs text-gray-600 mb-2">
                  Paste CSV or TSV. Columns: title, youtubeUrl, topic, description, tags. First row can be headers. Tags are comma-separated in a single cell.
                </p>
                <textarea
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  placeholder="title,youtubeUrl,topic,description,tags\nIntro to Accounting,https://youtu.be/xxxx,ICAG,Short desc,accounting; basics"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm min-h-[140px]"
                  disabled={isImporting}
                />
                <div className="mt-3 flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    {bulkText.trim()
                      ? `${parseBulkResources(bulkText).length} valid row(s) detected`
                      : 'No rows detected'}
                  </div>
                  <button
                    onClick={async () => {
                      if (!isAdmin) return;
                      const items = parseBulkResources(bulkText);
                      if (items.length === 0) {
                        toast.error('No valid rows found');
                        return;
                      }
                      setIsImporting(true);
                      let success = 0;
                      let failed = 0;
                      for (const item of items) {
                        try {
                          await addVideo(item as any);
                          success++;
                        } catch (e) {
                          failed++;
                        }
                      }
                      setIsImporting(false);
                      if (success) toast.success(`Imported ${success} resource(s)`);
                      if (failed) toast.error(`${failed} row(s) failed`);
                      if (failed === 0) setBulkText('');
                    }}
                    disabled={isImporting || !bulkText.trim()}
                    className="px-4 py-2 bg-primary-600 text-white text-sm font-semibold rounded-md disabled:opacity-50"
                  >
                    {isImporting ? 'Importing...' : 'Import'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Topic Filter */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Browse by Topic</h2>
          <div className="text-sm text-gray-600">
            {filteredVideos.length} resource{filteredVideos.length !== 1 ? 's' : ''} available
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:space-x-4">
          <label className="text-sm font-semibold text-gray-700">Filter by:</label>
        <CustomDropdown
          value={selectedTopic}
          onChange={setSelectedTopic}
          options={topics.map((t) => ({
            value: t,
            label: t.charAt(0).toUpperCase() + t.slice(1)
          }))}
          placeholder="Select Topic"
            className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg text-sm bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        </div>
      </div>

      {loading && (
        <div className="text-center py-12">
          <div className="inline-flex items-center px-6 py-4 bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 shadow-xl">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-4"></div>
            <span className="text-lg font-semibold text-gray-700">Loading amazing content...</span>
          </div>
        </div>
      )}
      {error && (
        <div className="text-center py-12">
          <div className="inline-flex items-center px-6 py-4 bg-red-50/80 backdrop-blur-sm rounded-2xl border border-red-200 shadow-xl">
            <svg className="w-8 h-8 text-red-500 mr-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-lg font-semibold text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Pagination controls (top) */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6">
        {paginatedVideos.map((v) => (
          <div key={v.id} className="bg-white/80 backdrop-blur-sm border border-white/20 rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            <div className="mb-3 sm:mb-4">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-2 sm:mb-3 gap-2">
                <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-800 leading-tight">{v.title}</h3>
                {v.topic && (
                  <span className="px-2 sm:px-3 py-1 bg-gradient-to-r from-blue-100 to-purple-100 text-blue-800 text-xs font-semibold rounded-full border border-blue-200 w-fit">
                    {v.topic}
                  </span>
                )}
              </div>
              {v.description && (
                <p className="text-xs sm:text-sm lg:text-base text-gray-600 leading-relaxed">{v.description}</p>
              )}
            </div>

            <YouTubePlayer videoUrl={v.youtubeUrl} />

            {/* Transcript Section */}
            <div className="mt-4 sm:mt-6 p-2 sm:p-3 lg:p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg sm:rounded-xl border border-blue-100">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 sm:mb-3 gap-2 sm:gap-3">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg mr-3">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h4 className="text-sm font-semibold text-gray-800">AI-Powered Transcript</h4>
                </div>
                <button
                  onClick={() => handleFetchTranscript(v.id || '', v.youtubeUrl)}
                  disabled={loadingTranscripts[v.id || ''] || !!transcripts[v.id || '']}
                  className={`w-full sm:w-auto px-4 py-2 text-xs font-medium rounded-lg disabled:opacity-50 transition-all duration-200 ${
                    transcripts[v.id || ''] === 'NO_TRANSCRIPT' 
                      ? 'bg-yellow-500 text-white hover:bg-yellow-600 shadow-lg' 
                      : 'bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:from-blue-600 hover:to-purple-600 shadow-lg hover:shadow-xl'
                  }`}
                >
                  {loadingTranscripts[v.id || ''] ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2"></div>
                      Loading...
                    </div>
                  ) : transcripts[v.id || ''] === 'NO_TRANSCRIPT' ? 'Not Available' :
                     transcripts[v.id || ''] ? 'Loaded' : 'Load Transcript'}
                </button>
              </div>
              {transcripts[v.id || ''] && transcripts[v.id || ''] !== 'NO_TRANSCRIPT' && (
                <div className="max-h-32 overflow-y-auto p-3 bg-white/60 backdrop-blur-sm rounded-lg border border-blue-200">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {transcripts[v.id || '']}
                  </p>
                </div>
              )}
              {transcripts[v.id || ''] === 'NO_TRANSCRIPT' && (
                <div className="p-3 bg-yellow-100/80 backdrop-blur-sm rounded-lg border border-yellow-200">
                  <p className="text-sm text-yellow-800 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                    Transcript not available for this video. YouTube may have disabled transcripts for this content.
                  </p>
                </div>
              )}
            </div>

            {/* Comments */}
            <div className="mt-4 sm:mt-6 p-2 sm:p-3 lg:p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg sm:rounded-xl border border-green-100">
              <div className="flex items-center mb-2 sm:mb-3">
                <div className="p-2 bg-green-100 rounded-lg mr-3">
                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <h4 className="text-sm font-semibold text-gray-800">Community Discussion</h4>
              </div>
              <div className="space-y-2 sm:space-y-3 max-h-40 overflow-y-auto">
                {(v.comments || []).map((c, idx) => (
                  <div key={idx} className="p-2 sm:p-3 bg-white/60 backdrop-blur-sm rounded-lg border border-green-200">
                    <div className="flex items-center justify-between mb-1 sm:mb-2">
                      <span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded-full">
                        {c.authorName}
                      </span>
                      <span className="text-xs text-gray-500">{new Date(c.createdAt).toLocaleString()}</span>
                    </div>
                    <div className="text-xs sm:text-sm text-gray-800 leading-relaxed" dangerouslySetInnerHTML={{ __html: renderTextWithLinks(c.content) }} />
                  </div>
                ))}
                {(v.comments || []).length === 0 && (
                  <div className="text-center py-3 sm:py-4 text-gray-500">
                    <svg className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <p className="text-xs sm:text-sm">No comments yet. Start the discussion!</p>
                  </div>
                )}
              </div>

              <div className="mt-3 sm:mt-4">
                {currentUser ? (
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                                    <input
                      type="text"
                      value={commentDrafts[v.id || ''] || ''}
                      onChange={(e) => setCommentDrafts((prev) => ({ ...prev, [v.id || '']: e.target.value }))}
                      placeholder="Share your thoughts..."
                      className="flex-1 px-2 sm:px-3 lg:px-4 py-2 sm:py-3 border border-green-200 rounded-lg text-sm bg-white/60 backdrop-blur-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                      disabled={!currentUser}
                    />
                    <button
                      onClick={async () => {
                        if (!v.id) return;
                        const text = (commentDrafts[v.id] || '').trim();
                        if (!text) return;
                        await addComment(v.id, text);
                        setCommentDrafts((prev) => ({ ...prev, [v.id || '']: '' }));
                      }}
                      disabled={!currentUser || !(commentDrafts[v.id || ''] || '').trim()}
                      className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-sm font-semibold rounded-lg disabled:opacity-50 hover:from-green-600 hover:to-emerald-600 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                    >
                      Post Comment
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-4 bg-white/40 rounded-lg border border-green-200">
                    <svg className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-2 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <p className="text-xs sm:text-sm text-gray-600">Sign in to join the discussion</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination controls (bottom) */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
      />

      {/* AI Chat Button */}
      <div className="mt-12 text-center">
      <AIChatButton />
      </div>

      {/* Footer */}
      <div className="mt-12 sm:mt-16 text-center py-6 sm:py-8 border-t border-gray-200/50">
        <div className="max-w-2xl mx-auto px-4">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
            Ready to accelerate your learning?
          </h3>
          <p className="text-sm sm:text-base text-gray-600 mb-6">
            Explore our comprehensive course catalog and take your professional development to the next level.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <button className="w-full sm:w-auto px-4 sm:px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm sm:text-base font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1">
              Browse All Courses
            </button>
            <button className="w-full sm:w-auto px-4 sm:px-6 py-3 border-2 border-gray-300 text-gray-700 text-sm sm:text-base font-semibold rounded-lg hover:border-gray-400 hover:bg-gray-50 transition-all duration-200">
              Learn More
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default FreeResources;


